import './answer-result-bar.css';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import {useGSAP} from "@gsap/react";
import {AnswerSource} from "../../redux/survey/survey.types";
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/redux-store';
import { toggleAnswerSelection } from '../../redux/survey/survey-selected-answers.slice';
import { useGetSurveyStatsQuery } from '../../redux/survey/survey.endpoints';
import { useDebounce } from 'src/hooks/use-debounce';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const PERCENTAGE_ANIMATION_DURATION = 0.5;
interface AnswerResultBarProps {
  answerLabel: string;
  hasImageAnswers?: boolean;
  imgSrc?: string | null;
  totalCount: number;
  userPercent: number;
  answerSource?: AnswerSource;
  useRedesign?: boolean;
  answerId: string;
  extSurveyId: string;
}

const AnswerResultBar: React.FC<AnswerResultBarProps> = ({
  answerLabel,
  answerSource,
  hasImageAnswers,
  imgSrc,
  totalCount,
  userPercent,
  useRedesign = false,
  answerId,
  extSurveyId,
}) => {
  const dispatch = useDispatch();
  const selectedAnswerIds = useSelector((state: RootState) => {
    return state.selectedAnswers.selectedAnswerIds;
  });
  const isSelected = selectedAnswerIds.includes(answerId);

  const debouncedSelectedAnswers = useDebounce(selectedAnswerIds, 500);

  const { refetch } = useGetSurveyStatsQuery(
    {
      ext_survey_id: extSurveyId,
      selected_survey_answers: debouncedSelectedAnswers,
    },
    {
      skip: debouncedSelectedAnswers.length === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const percentageRef = useRef<HTMLSpanElement | null>(null);
  const totalCountRef = useRef<HTMLSpanElement | null>(null);
  const answerRef = useRef<HTMLSpanElement | null>(null);
  const [accessoryWidth, setAccessoryWidth] = useState(0);
  const leftAccessoryRef = useRef<HTMLDivElement | null>(null);
  const rightAccessoryRef = useRef<HTMLDivElement | null>(null);
  const percentage = useMemo(() => parseFloat((userPercent ?? 0).toFixed(1)), [userPercent]);

  const handleClick = () => {
    dispatch(toggleAnswerSelection(answerId));
  };

  useGSAP(() => {
    gsap.to(barRef.current, {
      duration: PERCENTAGE_ANIMATION_DURATION,
      width: `${percentage}%`,
    });
    gsap.to(percentageRef.current, {
      duration: PERCENTAGE_ANIMATION_DURATION,
      scrambleText: {
        chars:"0123456789",
        text: `${percentage}`,
      },
    });
  }, [percentage]);

  useGSAP(() => {
    gsap.to(totalCountRef.current, {
      duration: PERCENTAGE_ANIMATION_DURATION,
      scrambleText: {
        chars:"0123456789",
        text: `${totalCount}`,
      },
    });
  }, [totalCount]);

  useLayoutEffect(() => {
    if (!rightAccessoryRef.current) {
      return;
    }

    const rightWidth = rightAccessoryRef.current.offsetWidth;
    const leftWidth = hasImageAnswers ? leftAccessoryRef.current?.offsetWidth || 0 : 0;
    setAccessoryWidth(Math.max(rightWidth, leftWidth));
  }, [percentage, totalCount, hasImageAnswers]);

  const answerColorsRGB = (): { answerBar: string, answerContainer: string} => {
    if (!useRedesign) {
      return {
        answerBar: 'rgba(6, 41, 195, 1)',
        answerContainer: 'rgba(50, 87, 251, 1)',
      };
    }
    if (answerSource === undefined) {
      return {
        answerBar: 'rgba(51, 88, 252, 1)',
        answerContainer: 'rgba(255, 255, 255, 0.2)',
      };
    }
    switch (answerSource) {
      case AnswerSource.Human:
        return {answerContainer: 'rgba(255, 255, 255, 0.2)', answerBar: 'rgba(255, 109, 45, 1)'};
      case AnswerSource.AI:
        return {answerContainer: 'rgba(255, 255, 255, 0.2)', answerBar: 'rgba(117, 35, 241, 1)'};
      default:
        return {answerContainer: 'rgba(255, 255, 255, 0.2)', answerBar: 'rgba(51, 88, 252, 1)'};
    }
  };

  useGSAP(() => {
    if (answerSource === undefined && useRedesign) {
      return;
    }
    const { answerBar, answerContainer } = answerColorsRGB();
    gsap.to(containerRef.current, {
      backgroundColor: answerContainer,
      delay: 0.1,
      duration: PERCENTAGE_ANIMATION_DURATION,
    });

    gsap.to(barRef.current, {
      backgroundColor: answerBar,
      delay: 0.1,
      duration: PERCENTAGE_ANIMATION_DURATION,
    });
  }, [answerSource]);

  const { answerBar, answerContainer } = answerColorsRGB();

  const containerClasses = `${hasImageAnswers ? 'h-20' : 'h-14'} w-full rounded-2xl relative overflow-hidden transition-all`;
  const containerStyle = {
    backgroundColor: answerContainer,
  };

  const barClasses = `w-0 ${percentage >= 98 ? 'rounded-2xl' : 'rounded-l-2xl'} ${hasImageAnswers ? 'h-20' : 'h-14'}`;
  const barStyle = {
    backgroundColor: answerBar,
  };

  const percentageClasses = `${!useRedesign ? "text-blue-light-700 bg-custom-blue-gray border" : "text-white bg-white/[.2]"} text-sm rounded-lg md:rounded-2xl py-0.5 md:py-0 px-2 flex flex-col sm:flex-row gap-0.5 items-center justify-center text-center`;

  const renderAnswerForeground = () => {
    return (
      <div className="absolute inset-0">
        <div className="flex flex-row items-center justify-between gap-2 z-10 px-2 w-full h-full">
          <div
            className="shrink-0"
            ref={leftAccessoryRef}
            style={{
              width: accessoryWidth > 0 ? accessoryWidth : 'auto',
            }}
          >
            {imgSrc && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <img
                      className="size-16 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      src={imgSrc}
                      alt=""
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="p-2 bg-transparent border-none shadow-none max-w-none"
                    sideOffset={8}
                  >
                    <img
                      src={imgSrc}
                      alt=""
                      className="max-w-sm max-h-72 object-contain rounded-lg shadow-2xl"
                    />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <span
            className={`answer-label overflow-hidden shrink text-center ${(useRedesign) ? 'text-white' : 'text-black'}`}
            ref={answerRef}
          >
            {answerLabel}
          </span>
          <div
            className={percentageClasses}
            ref={rightAccessoryRef}
            style={{
              minWidth: accessoryWidth > 0 ? accessoryWidth : 'auto',
            }}
          >
            <span>
              <span ref={percentageRef}>
                {percentage}
              </span>
              {/**/}
              %
            </span>
            <span>
              (
              {/**/}
              <span ref={totalCountRef}>
                {totalCount}
              </span>
              {/**/}
              )
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`w-full rounded-2xl bg-gray ${useRedesign ? '' : 'py-2'}`}
      style={isSelected ? { outline: '2px solid #EAB308' } : {}}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      <div
        className={containerClasses}
        ref={containerRef}
        style={containerStyle}
      >
        <div
          className={barClasses}
          ref={barRef}
          style={barStyle}
        />
        {renderAnswerForeground()}
      </div>
    </div>
  );
};

export default AnswerResultBar;
